import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { QuestionAnswerEditorViewModelFactory } from '@/features/jobs/questions/QuestionAnswerEditorViewModelFactory'
import type {
  Question,
  QuestionAnswer,
} from '@/features/jobs/questions/questions'
import { action, computed, observable } from 'mobx'
import { QuestionEditorViewModel } from '@/features/jobs/questions/QuestionEditorViewModel'
import { mergeMap, Subject } from 'rxjs'
import {
  distinctUntilChanged,
  find,
  map,
  share,
  takeUntil,
} from 'rxjs/operators'
import { deepEqual } from '@/utils/object-util'

/**
 * View-model for question editors.
 */
export class QuestionListViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _track$ = new Subject<QuestionEditorViewModel>()
  private readonly _untrack$ = new Subject<QuestionEditorViewModel>()

  /**
   * List of question view-models.
   *
   * @private
   */
  @observable
  private accessor _questionVms: QuestionEditorViewModel[] = []

  /**
   * Answers changes as a stream.
   *
   * Only emits when the answers get changed by the user, not just based
   * on the MobX answers observable.
   */
  readonly answersChanged$ = this._track$.pipe(
    // Whenever we start tracking a VM, subscribe to it's changes
    mergeMap((vm) =>
      vm.editor.answerChanged$.pipe(
        // ... until we untrack the VM.
        takeUntil(this._untrack$.pipe(find((x) => x === vm))),
      ),
    ),
    map(() => this.answers),
    // Only emit when the answers have changed structurally.
    distinctUntilChanged(deepEqual),
    // Only keep a single subscription.
    share(),
  )

  /**
   * Emits when a question requests that the user continue to the next
   * question.
   */
  readonly continue$ = this._track$.pipe(
    // Whenever we start tracking a VM, subscribe to it's changes
    mergeMap((vm) =>
      vm.editor.continue$.pipe(
        // ... until we untrack the VM.
        takeUntil(this._untrack$.pipe(find((x) => x === vm))),
      ),
    ),
    share(),
  )

  constructor(
    private readonly _editorViewModelFactory: QuestionAnswerEditorViewModelFactory,
  ) {
    // This subscription ensures `answersChanged$` and `continue$
    // remains active ('hot'), preventing tracked answer VMs
    // from ceasing to emit values if consuming VMs are deactivated and
    // reactivated. Without it, reconciliation will fail to re-track
    // already populated questions.
    this.answersChanged$.subscribe()
    this.continue$.subscribe()
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Question view models.
   */
  @computed get questionVms(): readonly QuestionEditorViewModel[] {
    return this._questionVms
  }

  /**
   * Answers from all questions. Does not include irrelevant answers.
   */
  @computed.struct
  get answers() {
    const answersMap = new Map<string, QuestionAnswer>()
    for (const vm of this._questionVms) {
      if (vm.editor.answer) {
        answersMap.set(vm.id, vm.editor.answer)
      }
    }

    return answersMap
  }

  /**
   * Whether a breaker question type is currently visible.
   */
  @computed
  get isBroken() {
    return this._questionVms.some((vm) => vm.editor.type === 'Breaker')
  }

  /**
   * Reconciles the list of questions with the view models.
   *
   * @param questions
   */
  @action.bound
  reconcile(questions: Question[]) {
    const newVms: QuestionEditorViewModel[] = []
    const inputIds = new Set(questions.map((q) => q.id))

    for (const existing of this._questionVms) {
      const isInInput = inputIds.has(existing.id)
      if (!isInInput) {
        this._lifecycle.remove(existing)
        this._untrack$.next(existing)
      }
    }

    // Sync the list.
    for (const question of questions) {
      const existing = this._questionVms.find((vm) => vm.id === question.id)
      if (existing?.editor.type === question.type) {
        existing.set(question)
        newVms.push(existing)
        continue
      }

      const editor =
        this._editorViewModelFactory.createAnswerEditorViewModel(question)
      const newInstance = new QuestionEditorViewModel(editor)
      this._lifecycle.manage(newInstance)
      this._track$.next(newInstance)
      newInstance.set(question)
      newVms.push(newInstance)
    }

    this._questionVms = newVms
  }
}
